// Packages
import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";

// MUI Components
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Avatar from "@material-ui/core/Avatar";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import Toolbar from "@material-ui/core/Toolbar";
import Grid from "@material-ui/core/Grid";
import AppBar from "@material-ui/core/AppBar";
import Snackbar from "@material-ui/core/Snackbar";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";

// Components
import PickAUserDialog from "components/PickAUserDialog";

// Context
import MeContext from "contexts/MeContext";

// Util
import * as userUtil from "util/userUtil";

// Services
import * as UserApi from "apis/UserApi";

// Styles
import { useStyles } from "./styles";

// Data
import ROLES, { getRoleByKey } from "enums/ROLES";

export default function AllUsersTable(props) {
  const classes = useStyles();

  const [loadingUserId, setLoadingUserId] = useState();
  const [loadingGeneric, setLoadingGeneric] = useState();
  const [users, setUsers] = useState(UserApi.getAllFromCache());
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [snack, setSnack] = React.useState({ open: false, note: null });
  const [nonce, setNonce] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [filterTerm, setFilterTerm] = useState('');
  const [showLength, setShowLength] = useState(0);
  const [hardUpdate, setHardUpdate] = useState(false);
  const [upSet, setUpSet] = useState(false);
  const [tempTerm, setTempTerm] = useState();

  const me = useContext(MeContext);

  function filterUsers(term){
    if(term && term.length > 0){
      const newUsers = [];
      for(const aUser in allUsers){
        if('firstName' in allUsers[aUser] && allUsers[aUser].firstName.toLowerCase().includes(term.toLowerCase())){
          newUsers.push(allUsers[aUser]);
        }
        else if('lastName' in allUsers[aUser] && allUsers[aUser].lastName.toLowerCase().includes(term.toLowerCase())){
          newUsers.push(allUsers[aUser]);
        }
        else if('email' in allUsers[aUser] && allUsers[aUser].email.toLowerCase().includes(term.toLowerCase())){
          newUsers.push(allUsers[aUser]);
        }
      }
      setUsers(newUsers);
      setShowLength(newUsers.length);
    }
    else{
      setUsers(allUsers);
      setShowLength(allUsers.length);
    }
  }
  useEffect(() => {
    if(filterTerm && filterTerm.length > 0){
      filterUsers(filterTerm);
    }
  }, [filterTerm]);
  

  useEffect(() => {
    async function fetchAllUsers() {
      setTempTerm(filterTerm);
      setFilterTerm("");
      const users = await UserApi.getAll().then((usersA) => {
        return usersA;
      });
      setUsers(users);
      setAllUsers(users);
      setFilterTerm(filterTerm);
      setTempTerm();

      if(filterTerm && filterTerm.length > 0){
        filterUsers(filterTerm);
        setHardUpdate(!hardUpdate);
      }
    }
    fetchAllUsers();
  }, [nonce]);

  useEffect(() => {
    if(users){
      setShowLength(users.length);
    }
    }, [users]);

  function getTableRow(user) {
    if (!user) return null;

    let picture;
    if (user.picture) {
      picture = user.picture;
    }

    const meString = me.userId === user.userId ? " (me)" : "";
    const disabledString = user.disabled === undefined ? "" : " (disabled)";

    const meRO = userUtil.canDoApp(me, ROLES.READ_ONLY);
    const meRW = userUtil.canDoApp(me, ROLES.READ_WRITE);
    const meADMIN = userUtil.canDoApp(me, ROLES.ADMIN);

    return (
      <React.Fragment key={`${user.userId}-section`}>
        {loadingUserId === user.userId && (
          <TableRow key={`${user.userId}-load`} className={classes.loadingRow}>
            <TableCell colSpan={5} className={classes.loadingCell}>
              <LinearProgress />
            </TableCell>
          </TableRow>
        )}
        <TableRow
          key={user.userId}
          hover={true}
          className={
            user.disabled === undefined ? undefined : classes.disabledRow
          }
        >
          <TableCell component="th" scope="row">
            <Avatar alt={userUtil.getDisplayName(user)} src={picture}>
              {!picture ? userUtil.getInitials(user) : null}
            </Avatar>
          </TableCell>
          <TableCell component={Link} to={userUtil.getProfileLink(user.userId)}>
            <Typography variant="body1">
              {userUtil.getDisplayName(user)}
              {meString}
              {disabledString}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography>{user.orgs ? user.orgs.length : 0}</Typography>
          </TableCell>
          <TableCell>
            <Typography>{getRoleForm(user)}</Typography>
          </TableCell>
          <TableCell>
            <Button
              variant="outlined"
              disabled={((meRO && !meRW && !meADMIN) || (me.userId === user.userId))}
              color={user.disabled === undefined ? "primary" : "secondary"}
              onClick={() =>
                handleDisableChange(user, user.disabled === undefined)
              }
            >
              {user.disabled !== undefined ? "Enable" : "Disable"}
            </Button>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  async function handleRoleChange(user, role) {
    setLoadingUserId(user.userId);
    const roleChangeResult  = await UserApi.updateAppRole(user.userId, role);
    let message = `${userUtil.getDisplayName(user)} role updated to ${role.title}`;
    if(roleChangeResult === 'false' || roleChangeResult === false || !roleChangeResult ){
      message = "Only Admins can update a user's role. Please contact your company administrator.";
    }
    setLoadingUserId();
    setSnack({
      open: true,
      note: message
    });
    setHardUpdate(!hardUpdate);

    setNonce(!nonce); //flip the bit to reload
    setUpSet(true);
  }

  async function handleDisableChange(user, disableUser) {
    setLoadingUserId(user.userId);
    if (disableUser) {
      await UserApi.disableUser(user.userId);
    } else {
      await UserApi.enableUser(user.userId);
    }
    setUsers(UserApi.getAllFromCache());
    setLoadingUserId();
  }

  function handleAddUser(user) {
    setLoadingGeneric(true);
    setShowAddUserDialog(false);
    setUsers(UserApi.getAllFromCache());
    setLoadingGeneric(false);
    setSnack({
      open: true,
      note: `${userUtil.getDisplayName(user)} has been created`,
    });
  }

  function getRoleByUser(user){
    let role = ROLES.READ_ONLY;
    if (user.apps) {
      const app = user.apps.find((app) => app.appName === "mss");
      if (app) {
        role = getRoleByKey(app.roleKey);
      }
    }
    return role;
  }

  function getRoleForm(user) {
    if (!userUtil.isEmployee(user)) return;

    const meRO = userUtil.canDoApp(me, ROLES.READ_ONLY);
    const meRW = userUtil.canDoApp(me, ROLES.READ_WRITE);
    const meADMIN = userUtil.canDoApp(me, ROLES.ADMIN);

    return (
      <FormControl
        variant="outlined"
        //disabled if not enabled or only have RO role
        disabled={meRO && !meRW && !meADMIN}
      >
        <Select
          value={getRoleByUser(user)}
          onChange={ async (event) => await handleRoleChange(user, event.target.value)}
          input={
            <OutlinedInput labelWidth={0} className={classes.roleKeySelector} />
          }
        >
          <MenuItem value={ROLES.ADMIN}>{ROLES.ADMIN.title}</MenuItem>
          <MenuItem value={ROLES.READ_WRITE}>{ROLES.READ_WRITE.title}</MenuItem>
          <MenuItem value={ROLES.READ_ONLY}>{ROLES.READ_ONLY.title}</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <React.Fragment>
      {
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={snack.open}
          onClose={() => setSnack({ open: false, note: null })}
          autoHideDuration={5000}
          ContentProps={{
            "aria-describedby": "message-id",
          }}
          message={<span>{snack.note}</span>}
        />
      }
      {loadingGeneric && <LinearProgress data-testid="progressBar" />}
      {users && (
        <Paper className={classes.root}>
          <AppBar position="static" color="default">
            <Toolbar className={classes.toolbar}>
              <Grid container justifyContent="space-between">
              {me && userUtil.isEmployee(me) && userUtil.isAdmin(me) && <Grid item xs={2}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    autoCorrect="off"
                    spellCheck="false"
                    label="Search"
                    placeholder="Search"
                    data-test="usersFilterSearch"
                    onChange={(event) => filterUsers(event.target.value)}
                  />
                </Grid>}

                <Grid item xs={8}>
                  <Typography
                    variant="h5"
                    component="h4"
                    data-testid="usersCountLabel" // FIXME: Deprecate data-testid
                    data-test="usersCountLabel"
                    className={classes.counter}
                  >
                    Users ({users.length})
                  </Typography>
                </Grid>
              </Grid>
            </Toolbar>
          </AppBar>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography>Avatar</Typography>
                </TableCell>
                <TableCell>
                  <Typography>Name</Typography>
                </TableCell>
                <TableCell>
                  <Typography>Orgs</Typography>
                </TableCell>
                <TableCell>
                  <Typography>MSS Role</Typography>
                </TableCell>
                <TableCell>
                  <Typography>Action</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                return getTableRow(user);
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
      {showAddUserDialog && (
        <PickAUserDialog
          handleClose={() => setShowAddUserDialog(false)}
          handleAddUser={handleAddUser}
          addText="Add"
        />
      )}
    </React.Fragment>
  );
}
